<template>
  <div class="report-container">
    <!-- Header -->
    <div class="report-header">
      <div>
        <h1 class="report-title">Daftar Transaksi</h1>
        <p class="report-subtitle">Kelola invoice & status pembayaran pelanggan.</p>
      </div>

      <div class="report-actions">
        <button class="btn-outline" @click="refresh">Refresh</button>
        <div class="date-range">
          <input type="date" v-model="exportStartDate" class="input-date">
          <span class="date-separator">s/d</span>
          <input type="date" v-model="exportEndDate" class="input-date">
        </div>
        <button class="btn-primary" @click="exportCSV">Export CSV</button>
      </div>
    </div>

    <!-- Filter -->
    <div class="report-filter">
      <div class="search-wrap">
        <input
          v-model.trim="q"
          type="text"
          class="search-input"
          placeholder="Cari invoice, customer, atau total..."
        />
      </div>

      <select v-model="statusFilter" class="filter-select">
        <option value="all">Semua Status</option>
        <option value="Belum Dibayar">Belum Dibayar</option>
        <option value="Sudah Dibayar">Sudah Dibayar</option>
      </select>

      <select v-model="sortBy" class="filter-select">
        <option value="date_desc">Tanggal • Terbaru</option>
        <option value="date_asc">Tanggal • Terlama</option>
        <option value="total_desc">Total • Tertinggi</option>
        <option value="total_asc">Total • Terendah</option>
      </select>
    </div>

    <!-- Summary cards -->
    <div class="report-summary">
      <div class="card">
        <div class="card-label">Total Invoice</div>
        <div class="card-value">{{ transaksi.length }}</div>
      </div>

      <div class="card">
        <div class="card-label">Belum Dibayar</div>
        <div class="card-value">
          {{ transaksi.filter(t => t.status !== 'Sudah Dibayar').length }}
        </div>
      </div>

      <div class="card">
        <div class="card-label">Sudah Dibayar</div>
        <div class="card-value">
          {{ transaksi.filter(t => t.status === 'Sudah Dibayar').length }}
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="report-table">
      <div v-if="loading" class="table-loading">Memuat data…</div>

      <div v-else class="table-scroll">
        <table>
          <thead>
            <tr>
              <th style="width:72px;">No</th>
              <th>Invoice</th>
              <th>Customer</th>
              <th style="width:120px;">Tanggal</th>
              <th class="th-right">Total</th>
              <th style="width:140px;">Status</th>
              <th style="width:130px;">Dibuat Oleh</th>
              <th style="width:220px;" class="th-right">Aksi</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(t, idx) in paged" :key="t.id || t.invoice_number + idx">
              <td class="muted">{{ (page-1)*perPage + idx + 1 }}</td>
              <td class="fw">{{ t.invoice_number }}</td>
              <td>{{ t.customer_name }}</td>
              <td>{{ formatDate(t.date) }}</td>
              <td class="td-right fw">{{ toIDR(t.total) }}</td>

              <td>
                <span
                  v-if="t.status !== 'Sudah Dibayar'"
                  class="badge rounded-pill bg-warning-subtle text-warning-emphasis border border-warning-subtle"
                >
                  {{ t.status }}
                </span>

                <span
                  v-else
                  class="badge rounded-pill bg-success-subtle text-success-emphasis border border-success-subtle"
                >
                  {{ formatDateTime(t.paid_at) }}
                </span>
              </td>

              <td>{{ t.created_by_name || '-' }}</td>

              <td class="td-right">
                <div class="action-buttons">
                  <button class="icon-btn dark" @click="downloadInvoiceJPG(t)">
                    <i class="bx bx-download"></i>
                  </button>

                  <template v-if="isAdminUser">
                    <button
                      v-if="t.status !== 'Sudah Dibayar'"
                      class="icon-btn green"
                      @click="openConfirm(t, 'Sudah Dibayar')"
                    >
                      <i class="bx bx-check-double"></i>
                    </button>

                    <button
                      v-else
                      class="icon-btn gray"
                      @click="openConfirm(t, 'Belum Dibayar')"
                    >
                      <i class="bx bx-undo"></i>
                    </button>
                  </template>

                  <button class="icon-btn blue" @click="editInvoice(t)">
                    <i class="bx bx-edit"></i>
                  </button>

                  <button v-if="isAdminUser" class="icon-btn red" @click="deleteInvoice(t.id || t.invoice_id)">
                    <i class="bx bx-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination (lo boleh keep BootstrapVue pagination, tapi wrap biar clean) -->
      <div v-if="filtered.length" class="pagination-bar">
        <div class="pagination-left">
          Menampilkan <b>{{ (page-1)*perPage + 1 }}</b>–<b>{{ Math.min(page*perPage, filtered.length) }}</b> dari <b>{{ filtered.length }}</b>
        </div>

        <div class="pagination-right">
          <select v-model.number="perPage" class="filter-select small">
            <option :value="5">5 / halaman</option>
            <option :value="10">10 / halaman</option>
            <option :value="20">20 / halaman</option>
          </select>

          <b-pagination
            v-model="page"
            :total-rows="filtered.length"
            :per-page="perPage"
            size="sm"
            first-number
            last-number
          />
        </div>
      </div>

    </div>

    <div v-if="error" class="error-box">{{ error }}</div>

    <b-modal
      v-model="showConfirm"
      title="Konfirmasi"
      ok-title="Ya, simpan"
      cancel-title="Batal"
      :ok-disabled="saving"
      :cancel-disabled="saving"
      @ok="confirmUpdate"
    >
      <p class="mb-0">
        Ubah status invoice <strong>{{ selectedRow?.invoice_number }}</strong>
        menjadi <b>{{ targetStatus }}</b>?
      </p>
    </b-modal>

  </div>
</template>

<script>
import { api } from '@/utils/api';
import html2canvas from 'html2canvas';
import Swal from "sweetalert2";
import { isAdmin } from "@/utils/auth";
export default {
  name: "TransaksiPage",
  data() {
    return {
      exportStartDate: "",
      exportEndDate: "",
      transaksi: [],
      loading: true,
      error: "",
      // UI state
      q: "",
      statusFilter: "all",
      sortBy: "date_desc",
      page: 1,
      perPage: 20,
      statusOptions: [
        { value: "all", text: "Semua Status" },
        { value: "Belum Dibayar", text: "Belum Dibayar" },
        { value: "Sudah Dibayar", text: "Sudah Dibayar" },
      ],
      sortOptions: [
        { value: "date_desc", text: "Tanggal • Terbaru" },
        { value: "date_asc", text: "Tanggal • Terlama" },
        { value: "total_desc", text: "Total • Tertinggi" },
        { value: "total_asc", text: "Total • Terendah" },
      ],
      perPageOptions: [
        { value: 5, text: "5 / halaman" },
        { value: 10, text: "10 / halaman" },
        { value: 20, text: "20 / halaman" },
      ],
      // expand state + cache detail
      expanded: new Set(),    // pakai ID sebagai key
      itemsCache: {},         // { [id]: { items: [...] } }
      rowLoading: {},         // { [id]: boolean }
      // modal status
      showConfirm: false,
      selectedRow: null,
      targetStatus: 'Sudah Dibayar',
      saving: false,
    };
  },
  computed: {
     isAdminUser() {
      return isAdmin();
    },
    filtered() {
      const q = this.q.toLowerCase().trim();
      const qNumber = q.replace(/\D/g, "");

      let arr = this.transaksi.filter(t => {
        const customerName = String(
          t.customer_name ||
          t.customer ||
          t.name ||
          t.nama_customer ||
          ""
        );

        const invoice = String(t.invoice_number || "");
        const phone = String(t.customer_phone || "");
        const totalRaw = String(t.total || "");
        const totalIDR = this.toIDR(t.total);

        const hayText = `
          ${invoice}
          ${customerName}
          ${phone}
          ${totalRaw}
          ${totalIDR}
        `.toLowerCase();

        const totalNumber = totalRaw.replace(/\D/g, "");

        return (
          hayText.includes(q) ||
          (qNumber && totalNumber.includes(qNumber))
        );
      });

      if (this.statusFilter !== "all") {
        arr = arr.filter(t => t.status === this.statusFilter);
      }

      const byDateNewest = (a,b)=> new Date(b.date) - new Date(a.date);
      const byDateOldest = (a,b)=> new Date(a.date) - new Date(b.date);
      const byTotal = (a,b)=> (parseInt(a.total)||0) - (parseInt(b.total)||0);

      switch (this.sortBy) {
        case "date_asc": arr.sort(byDateOldest); break;
        case "date_desc": arr.sort(byDateNewest); break;
        case "total_asc": arr.sort(byTotal); break;
        case "total_desc": arr.sort((a,b)=>byTotal(b,a)); break;
      }

      return arr;
    },

    paged() {
      const start = (this.page - 1) * this.perPage;
      return this.filtered.slice(start, start + this.perPage);
    },
  },
  watch: { q(){this.page=1}, statusFilter(){this.page=1}, sortBy(){this.page=1}, perPage(){this.page=1} },
  mounted() { this.refresh(); },
  methods: {
  downloadInvoiceJPG(t) {
    this.$router.push({
      name: "InvoiceDownload",
      query: { id: t.id || t.invoice_id } // sesuaikan id yang bener di data lo
    });
  },
    // --- Edit & Delete Invoice ---
    
      editInvoice(t) {
        // Arahkan ke halaman Invoice.vue dengan query id
        this.$router.push({ name: 'Invoice', query: { id: t.invoice_id || t.id } });
      },

      async deleteInvoice(id) {
        // 🟡 Konfirmasi dulu
        const confirm = await Swal.fire({
          title: "Yakin mau hapus?",
          text: "Invoice ini akan dihapus permanen dari database.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#ef4444",
          cancelButtonColor: "#6b7280",
          confirmButtonText: "Ya, hapus!",
          cancelButtonText: "Batal",
        });

        if (!confirm.isConfirmed) return;

        try {
          // 🔄 Munculin loading biar elegan
          Swal.fire({
            title: "Menghapus...",
            text: "Mohon tunggu sebentar.",
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          const res = await fetch(api("delete_invoice.php"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ invoice_id: id }),
          });

          const data = await res.json();

          // ✅ Berhasil
          if (data.success) {
            Swal.fire({
              icon: "success",
              title: "Invoice terhapus!",
              text: "Data invoice berhasil dihapus dari sistem.",
              confirmButtonColor: "#10b981",
              timer: 1500,
              showConfirmButton: false,
            });

            this.refresh(); // reload data list
          } else {
            Swal.fire({
              icon: "error",
              title: "Gagal menghapus!",
              text: data.message || "Terjadi kesalahan saat menghapus invoice.",
              confirmButtonColor: "#ef4444",
            });
          }
        } catch (err) {
          console.error("Gagal menghapus invoice:", err);
          Swal.fire({
            icon: "error",
            title: "Kesalahan jaringan!",
            text: "Tidak dapat terhubung ke server.",
            confirmButtonColor: "#ef4444",
          });
        }
      },

    // --- helpers expand ---
    keyOf(t) {
    // pakai id kalau ada; fallback ke invoice_number
    return String(t.id ?? t.invoice_number);
  },
    isExpanded(t) { return this.expanded.has(this.keyOf(t)); },
    detailItems(t) {
    const k = this.keyOf(t);
    return this.itemsCache[k]?.items ?? [];
  },
    detailLoading(t) {
    return !!this.rowLoading[this.keyOf(t)];
  },

      async toggleExpand(t) {
    const key = this.keyOf(t);

    // toggle
    if (this.expanded.has(key)) { this.expanded.delete(key); return; }
    this.expanded.add(key);

    // sudah ada di cache? jangan fetch lagi
    if (this.itemsCache[key]) return;

    this.rowLoading[key] = true;

    try {
      // <<< PENTING: gunakan qs yang benar
      const qs = t.id != null
        ? `invoice_id=${encodeURIComponent(t.id)}`
        : `invoice_number=${encodeURIComponent(t.invoice_number)}`;

      const res = await fetch(api(`get_invoice_items.php?${qs}`));
      const json = await res.json();

      this.itemsCache[key] = {
        items: (json.success && Array.isArray(json.data)) ? json.data : []
      };
    } catch (e) {
      console.error('fetch items error', e);
      this.itemsCache[key] = { items: [] };
    } finally {
      this.rowLoading[key] = false;
    }
  },


    // --- download invoice JPG ---
    async downloadInvoice(row) {
      const el = this.$refs[`printable-${row.id}`];
      const target = Array.isArray(el) ? el[0] : el; // Vue refs bisa array
      if (!target) {
        alert('Buka detail invoice dulu (klik panah) sebelum download.');
        return;
      }
      const canvas = await html2canvas(target, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        allowTaint: true,
        scrollY: 0
      });
      const link = document.createElement("a");
      link.download = `${row.invoice_number}.jpg`;
      link.href = canvas.toDataURL("image/jpeg");
      link.click();
    },

    // --- status update ---
    openConfirm(row, status) {
      this.selectedRow = row;
      this.targetStatus = status;
      this.showConfirm = true;
    },
    async confirmUpdate() {
      if (!this.selectedRow) return;
      this.saving = true;
      try {
        const res = await fetch(api('update_status.php'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: Number(this.selectedRow.id),
            status: this.targetStatus.trim()
          }),
        });
        const data = await res.json();
        if (data.success) {
          this.selectedRow.status = this.targetStatus;
        } else {
          alert(data.message || 'Gagal mengubah status');
        }
      } catch (err) {
        console.error('Update status error:', err);
        alert('Terjadi kesalahan jaringan');
      } finally {
        this.saving = false;
        this.showConfirm = false;
      }
    },

    // --- data load & utils ---
    async refresh() {
      try {
        this.loading = true; this.error = "";
        const res = await fetch(api('get_transaksi.php'));
        const data = await res.json();
        if (data.success) this.transaksi = data.data || [];
        else this.error = data.message || "Gagal memuat data.";
      } catch (e) {
        console.error(e); this.error = "Terjadi kesalahan jaringan.";
      } finally { this.loading = false; }
    },
    rowTotal(items) {
      return (items || []).reduce((s, it) =>
        s + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);
    },
    toIDR(val) {
      const n = Number(val) || 0;
      return `Rp${n.toLocaleString("id-ID")}`;
    },
    formatDate(iso) {
      const d = new Date(iso);
      if (isNaN(d)) return iso;

      return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "2-digit",
      });
    },
    formatDateTime(iso) {
      if (!iso) return "";

      const d = new Date(iso);
      if (isNaN(d)) return iso;

      const datePart = d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "2-digit",
      }).replace(".", "");

      const timePart = d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      return `${datePart} • ${timePart}`;
    },  
    exportCSV() {
      let data = this.filtered;

      if (this.exportStartDate) {
        data = data.filter(t => new Date(t.date) >= new Date(this.exportStartDate));
      }

      if (this.exportEndDate) {
        const end = new Date(this.exportEndDate);
        end.setHours(23, 59, 59, 999);
        data = data.filter(t => new Date(t.date) <= end);
      }

      const rows = [
        ["No","Invoice","Customer","Tanggal","Total","Status"],
        ...data.map((t,i)=>[
          i+1,
          t.invoice_number,
          t.customer_name,
          this.formatDate(t.date),
          this.toIDR(t.total),
          t.status
        ]),
      ];

      const csv = rows
        .map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(','))
        .join('\n');

      const blob = new Blob([csv], {type:"text/csv;charset=utf-8;"});
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `transaksi-${this.exportStartDate || 'awal'}-${this.exportEndDate || 'akhir'}.csv`;
      a.click();

      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.report-actions{
  display:flex;
  align-items:center;
  gap:10px;
  flex-wrap:wrap;
}

.input-date{
  height:42px;
  padding:0 12px;
  border-radius:12px;
  border:1px solid #e2e8f0;
  background:#fff;
  color:#0f172a;
  font-size:14px;
  font-family:inherit;
  cursor:pointer;
  transition:.15s ease;
}

.input-date:hover{
  border-color:#cbd5e1;
  background:#f8fafc;
}

.input-date:focus{
  outline:none;
  border-color:#6366f1;
  box-shadow:0 0 0 3px rgba(99,102,241,.12);
}

.input-date::-webkit-calendar-picker-indicator{
  cursor:pointer;
  opacity:.65;
}

.btn-primary,
.btn-outline{
  height:42px;
  padding:0 14px;
  border-radius:12px;
  font-size:14px;
  font-family:inherit;
  white-space:nowrap;
}
.date-range{
  display:flex;
  align-items:center;
  gap:8px;
}

.date-separator{
  color:#64748b;
  font-size:13px;
}
/* ===== base ===== */
.report-container{
  padding:40px;
  background:#f8fafc;
  min-height:100vh;
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
  color:#0f172a;
}

.report-header{
  display:flex;
  justify-content:space-between;
  align-items:flex-end;
  gap:16px;
  margin-bottom:18px;
  flex-wrap:wrap;
}

.report-title{
  font-size:26px;
  font-weight:650;
  letter-spacing:-.5px;
  margin:0 0 6px;
}

.report-subtitle{
  margin:0;
  color:#64748b;
  font-size:14px;
}

.report-actions{
  display:flex;
  gap:10px;
}

.btn-primary,.btn-outline{
  padding:10px 14px;
  border-radius:12px;
  font-size:14px;
  border:1px solid #e2e8f0;
  background:#fff;
  cursor:pointer;
  transition:.15s ease;
}
.btn-primary{
  background:#6366f1;
  border-color:#6366f1;
  color:#fff;
}
.btn-primary:hover{ background:#4f46e5; transform:translateY(-1px); }
.btn-outline:hover{ background:#f1f5f9; transform:translateY(-1px); }

/* ===== filter ===== */
.report-filter{
  display:flex;
  gap:12px;
  flex-wrap:wrap;
  margin-bottom:18px;
}

.search-wrap{ flex:1; min-width:240px; }
.search-input{
  width:100%;
  padding:10px 14px;
  border-radius:12px;
  border:1px solid #e2e8f0;
  background:#fff;
  font-size:14px;
}
.search-input:focus{
  outline:none;
  border-color:#6366f1;
  box-shadow:0 0 0 3px rgba(99,102,241,.12);
}

.filter-select{
  padding:10px 14px;
  border-radius:12px;
  border:1px solid #e2e8f0;
  background:#fff;
  font-size:14px;
}
.filter-select.small{ padding:8px 10px; font-size:13px; }

/* ===== summary cards ===== */
.report-summary{
  display:grid;
  grid-template-columns:repeat(auto-fit,minmax(220px,1fr));
  gap:14px;
  margin-bottom:18px;
}

.card{
  background:#fff;
  border-radius:18px;
  padding:18px 18px;
  box-shadow:0 6px 20px rgba(15,23,42,.05);
  transition:.2s ease;
}
.card:hover{
  transform:translateY(-3px);
  box-shadow:0 10px 28px rgba(15,23,42,.08);
}
.card-label{
  font-size:12px;
  color:#64748b;
  letter-spacing:.06em;
  text-transform:uppercase;
  margin-bottom:8px;
}
.card-value{
  font-size:20px;
  font-weight:700;
  font-variant-numeric:tabular-nums;
}

/* ===== table ===== */
.report-table{
  background:#fff;
  border-radius:18px;
  box-shadow:0 6px 20px rgba(15,23,42,.05);
  overflow:hidden;
}

.table-loading{
  padding:18px;
  color:#64748b;
}

.table-scroll{ overflow:auto; }

table{
  width:100%;
  border-collapse:collapse;
  font-size:14px;
}

thead{ background:#f1f5f9; }
th{
  text-align:left;
  padding:14px 16px;
  font-weight:650;
  color:#334155;
  font-size:13px;
}
td{
  padding:14px 16px;
  border-top:1px solid #f1f5f9;
  color:#475569;
}
tbody tr:hover{ background:#f8fafc; }

.th-right,.td-right{ text-align:right; }
.muted{ color:#94a3b8; }
.fw{ font-weight:650; }

/* ===== pill status ===== */
.pill{
  display:inline-flex;
  align-items:center;
  padding:6px 10px;
  border-radius:999px;
  font-size:12px;
  font-weight:650;
}
.pill-yellow{ background:rgba(245,158,11,.14); color:#92400e; }
.pill-green{ background:rgba(34,197,94,.14); color:#166534; }

/* ===== action buttons ===== */
.action-buttons{
  display:flex;
  justify-content:flex-end;
  gap:8px;
  flex-wrap:nowrap;
}

.icon-btn{
  width:36px;
  height:36px;
  border-radius:12px;
  display:flex;
  align-items:center;
  justify-content:center;
  border:0;
  cursor:pointer;
}
.icon-btn i{ font-size:16px; line-height:1; color:#fff; }

.icon-btn.dark{ background:#111827; }
.icon-btn.green{ background:#16a34a; }
.icon-btn.blue{ background:#0ea5e9; }
.icon-btn.red{ background:#ef4444; }
.icon-btn.gray{ background:#94a3b8; }
.icon-btn:hover{ filter:brightness(.95); transform:translateY(-1px); transition:.15s ease; }

/* ===== pagination ===== */
.pagination-bar{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:14px 16px;
  border-top:1px solid #f1f5f9;
  flex-wrap:wrap;
}
.pagination-left{ color:#64748b; font-size:13px; }
.pagination-right{ display:flex; align-items:center; gap:10px; }

.error-box{
  margin-top:14px;
  background:#fee2e2;
  color:#991b1b;
  padding:12px 14px;
  border-radius:12px;
  border:1px solid rgba(239,68,68,.25);
}

/* ===== responsive ===== */
@media (max-width: 768px){
  .report-container{ padding:20px; }
  th,td{ padding:12px 12px; }
}
</style>
